import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/shop", label: "Shop all" },
  { to: "/shop?category=tshirts", label: "T-shirts" },
  { to: "/shop?category=hoodies", label: "Hoodies" },
  { to: "/shop?category=accessories", label: "Accessories" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const accountTo = user ? (user.role === "admin" ? "/admin" : "/account") : "/login";
  const accountLabel = user ? (user.role === "admin" ? "Admin" : "Account") : "Sign in";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${scrolled ? "border-b border-line bg-bone/85 backdrop-blur-xl" : "border-b border-transparent"}`}
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 md:h-20 md:px-10">
          <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <NavLink key={l.label} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`} className="link-underline text-sm">
                {l.label}
              </NavLink>
            ))}
            <NavLink to="/track" data-testid="nav-link-track" className="link-underline text-sm">Track</NavLink>
          </nav>
          <div className="flex items-center gap-5">
            <Link to={accountTo} data-testid="nav-account-link" className="link-underline hidden text-sm sm:inline">{accountLabel}</Link>
            <button
              data-testid="nav-cart-button"
              onClick={() => setOpen(true)}
              className="group flex items-center gap-2 rounded-full border border-ink px-4 py-2 text-sm transition-colors duration-300 hover:bg-ink hover:text-bone"
            >
              Bag
              <span data-testid="nav-cart-count" className="font-mono text-xs">({count})</span>
            </button>
            <button data-testid="nav-menu-button" onClick={() => setMenu(true)} className="lg:hidden" aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {menu && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] flex flex-col bg-ink px-6 py-6 text-bone"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between">
              <Logo invert />
              <button data-testid="mobile-menu-close" onClick={() => setMenu(false)} aria-label="Close menu"><X className="h-7 w-7" /></button>
            </div>
            <nav className="mt-16 flex flex-col gap-4">
              {[...LINKS, { to: accountTo, label: accountLabel }].map((l, i) => (
                <motion.div key={l.label} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 + i * 0.06 }}>
                  <Link to={l.to} onClick={() => setMenu(false)} data-testid={`mobile-link-${i}`} className="font-display text-5xl italic">
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
